import type { PresetDateOption } from '../DateMenu';
import { DateMenu } from '../DateMenu';

import { Button } from '@simplywallst/ui-core/next';

import { DateRangePicker } from '@/features/portfolio/components/DateRangePicker';
import type { DateRange } from '../../hooks/useDateRangeState';
import { useState } from 'react';

const DateEnGB = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const PresetDateDisplay = ({
  start,
  end,
  inceptionStartDate,
}: {
  start: Date | null;
  end: Date | null;
  inceptionStartDate: Date;
}) => {
  if (start === null || end === null) {
    return (
      <div className="mt-x0_5 flex items-center gap-x2">
        <span className="text-xs text-solid">
          {DateEnGB.format(inceptionStartDate)}
        </span>
        <span className="text-xs text-solid">→</span>
        <span className="text-xs text-solid">
          {DateEnGB.format(new Date())}
        </span>
      </div>
    );
  }
  return (
    <div className="mt-x0_5 flex items-center gap-x2">
      <span className="text-xs text-solid">{DateEnGB.format(start)}</span>
      <span className="text-xs text-solid">→</span>
      <span className="text-xs text-solid">{DateEnGB.format(end)}</span>
    </div>
  );
};

type Props = {
  selectedOption: PresetDateOption;
  setSelectedOption: (option: PresetDateOption) => void;
  dateRange: DateRange;
  onChange: (dateRange: { start: Date; end: Date }) => void;
  onSubmit: () => void;
  isDisabled: boolean;
  inceptionStartDate: Date;
};

export const DateSelector = ({
  selectedOption,
  setSelectedOption,
  dateRange,
  onChange,
  onSubmit,
  isDisabled,
  inceptionStartDate,
}: Props) => {
  const isPresetDate = selectedOption !== 'custom';
  const [isError, setIsError] = useState(false);

  return (
    <div className="flex flex-col gap-x1 sm:flex-row md:gap-x2">
      <DateMenu
        isDisabled={isDisabled}
        selected={selectedOption}
        setSelected={setSelectedOption}
      />
      {isPresetDate ? (
        <PresetDateDisplay
          start={dateRange.start}
          end={dateRange.end}
          inceptionStartDate={inceptionStartDate}
        />
      ) : (
        <div className="flex flex-1 flex-col gap-y-x1 sm:flex-row sm:gap-x-x2">
          <DateRangePicker
            minValue={new Date(1900, 1, 1)}
            maxValue={new Date()}
            lang="en-GB"
            aria-label="Date range"
            onChange={onChange}
            defaultValue={{
              start: dateRange.start ?? new Date(),
              end: dateRange.end ?? new Date(),
            }}
            onValidationChange={(validationState) => {
              setIsError(!validationState.isValid);
            }}
          />
          <div className="flex w-inherit content-start justify-end sm:w-auto sm:justify-start">
            <Button
              variant="primary"
              onClick={onSubmit}
              isDisabled={isDisabled || isError}
              className="w-full"
            >
              Update
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
